import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Star, Loader2, CheckCircle2, Car, ThumbsUp, ThumbsDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FeedbackPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbackInfo, setFeedbackInfo] = useState(null);
  const [error, setError] = useState(null);
  
  // Rating state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState('');
  const [serviceQuality, setServiceQuality] = useState(0);
  const [communication, setCommunication] = useState(0);
  const [valueForMoney, setValueForMoney] = useState(0);
  const [wouldRecommend, setWouldRecommend] = useState(null);

  useEffect(() => {
    fetchFeedbackInfo();
  }, [token]);

  const fetchFeedbackInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/feedback/${token}`);
      const data = await response.json();
      
      if (data.already_submitted) {
        setSubmitted(true);
      } else if (response.ok) {
        setFeedbackInfo(data);
      } else {
        setError(data.detail || 'Feedback link not found');
      }
    } catch (err) {
      setError('Failed to load feedback form');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/feedback/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          review: review || null,
          service_quality: serviceQuality || null,
          communication: communication || null,
          value_for_money: valueForMoney || null,
          would_recommend: wouldRecommend
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Thank you for your feedback!');
      } else {
        toast.error(data.detail || 'Failed to submit feedback');
      }
    } catch (err) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ value, onChange, size = 'lg', label }) => {
    const [hover, setHover] = useState(0);
    const starSize = size === 'lg' ? 'w-10 h-10' : 'w-6 h-6';
    
    return (
      <div className="flex flex-col items-center gap-2">
        {label && <span className="text-sm text-slate-600">{label}</span>}
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => onChange(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
              className="focus:outline-none transition-transform hover:scale-110"
            >
              <Star
                className={`${starSize} ${
                  star <= (hover || value)
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'fill-slate-200 text-slate-300'
                } transition-colors`}
              />
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white border-gray-200">
          <CardContent className="py-12 text-center">
            <div className="text-6xl mb-4">😕</div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Oops!</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white border-gray-200">
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Thank You!</h2>
            <p className="text-slate-600 mb-4">
              Your feedback has been submitted successfully. We truly appreciate you taking the time to share your experience.
            </p>
            <div className="flex justify-center gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-8 h-8 ${
                    star <= rating ? 'fill-yellow-400 text-yellow-400' : 'fill-slate-200 text-slate-300'
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-slate-500">
              Your feedback helps us improve our service!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 py-8 px-4">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Rate Your Experience</h1>
          <p className="text-slate-600">at <span className="font-semibold text-orange-600">{feedbackInfo?.garage_name}</span></p>
        </div>

        {/* Vehicle Info */}
        <Card className="bg-white border-gray-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Car className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">
                  {feedbackInfo?.vehicle_info?.make} {feedbackInfo?.vehicle_info?.model}
                </p>
                <p className="text-sm text-slate-600">{feedbackInfo?.vehicle_info?.number}</p>
                <p className="text-xs text-slate-500">Job: {feedbackInfo?.job_number}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Rating */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-center text-lg text-slate-800">Overall Experience</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="focus:outline-none transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-12 h-12 ${
                      star <= (hoverRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'fill-slate-200 text-slate-300'
                    } transition-colors`}
                  />
                </button>
              ))}
            </div>
            <p className="text-sm text-slate-600">
              {rating === 0 && 'Tap to rate'}
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Very Good'}
              {rating === 5 && 'Excellent!'}
            </p>
          </CardContent>
        </Card>

        {/* Detailed Ratings */}
        {rating > 0 && (
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600">Rate specific aspects (optional)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <StarRating
                  value={serviceQuality}
                  onChange={setServiceQuality}
                  size="sm"
                  label="Service Quality"
                />
                <StarRating
                  value={communication}
                  onChange={setCommunication}
                  size="sm"
                  label="Communication"
                />
                <StarRating
                  value={valueForMoney}
                  onChange={setValueForMoney}
                  size="sm"
                  label="Value for Money"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Would Recommend */}
        {rating > 0 && (
          <Card className="bg-white border-gray-200">
            <CardContent className="py-4">
              <p className="text-center text-slate-700 mb-3">Would you recommend this garage?</p>
              <div className="flex justify-center gap-4">
                <Button
                  variant={wouldRecommend === true ? 'default' : 'outline'}
                  onClick={() => setWouldRecommend(true)}
                  className={wouldRecommend === true ? 'bg-green-500 hover:bg-green-600' : ''}
                >
                  <ThumbsUp className="w-4 h-4 mr-2" /> Yes
                </Button>
                <Button
                  variant={wouldRecommend === false ? 'default' : 'outline'}
                  onClick={() => setWouldRecommend(false)}
                  className={wouldRecommend === false ? 'bg-red-500 hover:bg-red-600' : ''}
                >
                  <ThumbsDown className="w-4 h-4 mr-2" /> No
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Review Text */}
        {rating > 0 && (
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600">Share your experience (optional)</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={review}
                onChange={(e) => setReview(e.target.value)}
                placeholder="Tell us about your experience..."
                rows={4}
                className="resize-none bg-white"
              />
            </CardContent>
          </Card>
        )}

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={rating === 0 || submitting}
          className="w-full py-6 text-lg bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
        >
          {submitting ? (
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
          ) : (
            <Star className="w-5 h-5 mr-2" />
          )}
          Submit Feedback
        </Button>

        <p className="text-center text-xs text-slate-500">
          Your feedback is valuable and helps us improve our service
        </p>
      </div>
    </div>
  );
};

export default FeedbackPage;
